import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search, Eye } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { useToast } from '../../../contexts/ToastContext';
import {
  obtenerHistorial, obtenerDetalle,
  type PaginaHistorial, type FiltrosHistorial, type CorreoDetalle,
} from '../../../services/correoService';
import { mensajeError } from './errores';
import { EstadoChip } from './ui';
import { fmtFecha } from './formato';
import styles from './CorreoModule.module.css';

/** RF-03 — Reportes / historial de correos con filtros y detalle. */
const ESTADOS = ['', 'EnCola', 'Programado', 'Enviado', 'Entregado', 'Rebotado', 'Queja', 'NoEntregable'];

export default function HistorialCorreoPage() {
  const { addToast } = useToast();
  const [filtros, setFiltros] = useState<FiltrosHistorial>({ pagina: 1, tamano: 25 });
  const [pagina, setPagina] = useState<PaginaHistorial | null>(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState<CorreoDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  // Anti-carrera de paginación: clics rápidos en Anterior/Siguiente → solo la última pedida pinta.
  const reqId = useRef(0);
  const cargar = useCallback(async (p = filtros.pagina ?? 1) => {
    const id = ++reqId.current;
    setCargando(true);
    try {
      const r = await obtenerHistorial({ ...filtros, pagina: p });
      if (id === reqId.current) setPagina(r);
    } catch (err) {
      if (id === reqId.current) addToast('error', mensajeError(err));
    } finally {
      if (id === reqId.current) setCargando(false);
    }
  }, [filtros, addToast]);

  // Carga inicial una sola vez; luego el usuario recarga con el botón «Buscar».
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(1); }, []);

  // El detalle también se secuencia: cerrar (o abrir otro) invalida el fetch en vuelo, así
  // una respuesta tardía no reabre el modal ya cerrado.
  const detalleReq = useRef(0);
  const verDetalle = async (envioId: string) => {
    const id = ++detalleReq.current;
    setDetalle(null);
    setCargandoDetalle(true);
    try {
      const d = await obtenerDetalle({ envioId });
      if (id === detalleReq.current) setDetalle(d);
    } catch (err) {
      if (id === detalleReq.current) addToast('error', mensajeError(err));
    } finally {
      if (id === detalleReq.current) setCargandoDetalle(false);
    }
  };
  const cerrarDetalle = () => {
    detalleReq.current++; // invalida cualquier carga en vuelo
    setDetalle(null);
    setCargandoDetalle(false);
  };

  const totalPaginas = pagina ? Math.max(1, Math.ceil(pagina.total / pagina.tamano)) : 1;

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.campo}>
          <label htmlFor="to">Destinatario</label>
          <input id="to" type="email" placeholder="correo@dominio" value={filtros.to ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, to: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="h-desde">Desde</label>
          <input id="h-desde" type="date" value={filtros.desde ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="h-hasta">Hasta</label>
          <input id="h-hasta" type="date" value={filtros.hasta ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="h-estado">Estado</label>
          <select id="h-estado" value={filtros.estado ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, estado: e.target.value }))}>
            {ESTADOS.map((s) => <option key={s} value={s}>{s || 'Todos'}</option>)}
          </select>
        </div>
        <div className={styles.campo}>
          <label htmlFor="h-tipo">Tipo</label>
          <input id="h-tipo" type="text" placeholder="p. ej. PIN_ACCESO" value={filtros.tipo ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value }))} />
        </div>
        <Button variant="secondary" size="sm" onClick={() => cargar(1)} disabled={cargando}>
          <Search size={15} /> Buscar
        </Button>
      </div>

      {cargando ? (
        <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>
      ) : !pagina || pagina.items.length === 0 ? (
        <div className={styles.vacio}>No hay correos para el filtro seleccionado.</div>
      ) : (
        <>
          <div className={styles.tablaScroll}>
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th>Fecha</th><th>Tipo</th><th>Asunto</th><th>Destinatario</th><th>Estado</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pagina.items.map((c) => (
                  <tr key={c.envioId}>
                    <td>{fmtFecha(c.fechaRecibidoUtc)}</td>
                    <td><span className={styles.chip}>{c.tipo}</span></td>
                    <td>{c.asunto}</td>
                    <td>{c.para.join(', ')}</td>
                    <td><EstadoChip estado={c.estado} /></td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => verDetalle(c.envioId)}>
                        <Eye size={15} /> Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.acciones} style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
            <span className={styles.kpiEtiqueta}>{pagina.total} correos · página {pagina.pagina} de {totalPaginas}</span>
            <Button variant="ghost" size="sm" disabled={(pagina.pagina) <= 1}
              onClick={() => cargar(pagina.pagina - 1)}>Anterior</Button>
            <Button variant="ghost" size="sm" disabled={pagina.pagina >= totalPaginas}
              onClick={() => cargar(pagina.pagina + 1)}>Siguiente</Button>
          </div>
        </>
      )}

      <Modal open={detalle !== null || cargandoDetalle} onClose={cerrarDetalle}
        title="Detalle del correo" size="lg">
        {cargandoDetalle || !detalle ? (
          <div className={styles.vacio}><Loader2 className="spinner" size={24} /></div>
        ) : (
          <div>
            <p><strong>Asunto:</strong> {detalle.asunto}</p>
            <p><strong>Tipo:</strong> {detalle.tipo} · <strong>Clase:</strong> {detalle.clase} · <strong>Estado:</strong> <EstadoChip estado={detalle.estado} /></p>
            <p><strong>Recibido:</strong> {fmtFecha(detalle.fechaRecibidoUtc)}
              {detalle.fechaEnviadoUtc && <> · <strong>Enviado:</strong> {fmtFecha(detalle.fechaEnviadoUtc)}</>}</p>
            {detalle.motivoFallo && <p className={styles.error}>Motivo de fallo: {detalle.motivoFallo}</p>}
            <p><strong>Destinatarios:</strong> {detalle.destinatarios.map((d) => `${d.direccion} (${d.tipo})${d.suprimido ? ' · suprimido' : ''}`).join(', ')}</p>

            <div className={styles.seccionTitulo} style={{ marginTop: '1rem' }}>Eventos</div>
            {detalle.eventos.length === 0 ? (
              <div className={styles.vacio}>Aún sin eventos registrados.</div>
            ) : (
              <div className={styles.tablaScroll}>
                <table className={styles.tabla}>
                  <thead><tr><th>Evento</th><th>Fecha</th></tr></thead>
                  <tbody>
                    {detalle.eventos.map((ev, i) => (
                      <tr key={i}><td>{ev.tipo}</td><td>{fmtFecha(ev.fechaUtc)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {detalle.cuerpo?.cuerpoHtml && (
              <>
                <div className={styles.seccionTitulo} style={{ marginTop: '1rem' }}>Contenido</div>
                <iframe title="Contenido del correo" sandbox="" style={{ width: '100%', height: 320, border: '1px solid var(--color-border-soft)', borderRadius: 8 }}
                  srcDoc={detalle.cuerpo.cuerpoHtml} />
              </>
            )}
            {detalle.cuerpo === null && (
              <p className={styles.kpiEtiqueta} style={{ marginTop: '1rem' }}>
                El contenido ya fue purgado por la política de retención.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
